import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Button,
    DialogActions,
    CircularProgress,
    TextField,
    Switch,
    FormControlLabel,
    InputAdornment,
    IconButton
} from "@material-ui/core";
import { Cached, FileCopy } from "@material-ui/icons";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { generateToken } from "../../utils/generateToken";
import copyToClipboard from "../../utils/copyToClipboard";

const useStyles = makeStyles((theme) => ({
    root: { display: "flex", flexWrap: "wrap" },
    multFieldLine: {
        display: "flex",
        "& > *:not(:last-child)": { marginRight: theme.spacing(1) },
    },
    tokenFieldLine: {
        display: "flex",
        alignItems: "flex-end",
        gap: theme.spacing(1),
        "& > *:first-child": { flex: 1 },
    },
    btnWrapper: { position: "relative" },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
}));

const Schema = Yup.object().shape({
    name: Yup.string().min(2).required(),
    address: Yup.string().required(),
    backendDir: Yup.string().required(),
    CHROME_BIN: Yup.string().required(),
    token: Yup.string().required(),
});

const LocaleServerModal = ({ open, onClose, localeServerId }) => {
    const classes = useStyles();

    const initialState = {
        name: "",
        address: "",
        backendDir: "",
        enabled: true,
        status: true,
        CHROME_BIN: "/usr/bin/google-chrome-stable",
        token: "",
    };

    const [localeServer, setLocaleServer] = useState(initialState);

    useEffect(() => {
        const fetchData = async () => {
            if (!localeServerId) return;
            try {
                const { data } = await api.get(`/localeServers/${localeServerId}`);
                setLocaleServer(data);
            } catch (err) {
                toastError(err);
            }
        };
        fetchData();
    }, [localeServerId]);

    const handleSave = async values => {
        try {
            if (localeServerId) {
                await api.put(`/localeServers/${localeServerId}`, values);
            } else {
                await api.post("/localeServers", values);
            }
            toast.success(i18n.t("global.success"));
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    const handleClose = () => {
        onClose();
        setLocaleServer(initialState);
    };

    const handleGenerateToken = (setFieldValue) => {
        const newToken = generateToken();
        setFieldValue("token", newToken);
        toast.info(i18n.t("global.success"));
    };
    const handleCopyToken = async (token) => {
        await copyToClipboard(token);
    };
    return (
        <div className={classes.root}>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
                <DialogTitle>{localeServerId ? "Editar Locale Server" : "Adicionar Locale Server"}</DialogTitle>
                <Formik
                    initialValues={localeServer}
                    enableReinitialize
                    validationSchema={Schema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSave(values);
                            actions.setSubmitting(false);
                        }, 300);
                    }}
                >
                    {({ values, touched, errors, isSubmitting, setFieldValue }) => (
                        <Form>
                            <DialogContent dividers>
                                <Field as={TextField} fullWidth label="Nome" name="name" variant="outlined" margin="dense" error={touched.name && Boolean(errors.name)} helperText={touched.name && errors.name} />
                                <Field as={TextField} fullWidth label="Endereço" name="address" variant="outlined" margin="dense" error={touched.address && Boolean(errors.address)} helperText={touched.address && errors.address} />
                                <Field as={TextField} fullWidth label="Backend Dir" name="backendDir" variant="outlined" margin="dense" error={touched.backendDir && Boolean(errors.backendDir)} helperText={touched.backendDir && errors.backendDir} />
                                <Field as={TextField} fullWidth label="Chrome Bin" name="CHROME_BIN" variant="outlined" margin="dense" error={touched.CHROME_BIN && Boolean(errors.CHROME_BIN)} helperText={touched.CHROME_BIN && errors.CHROME_BIN} />
                                <div className={classes.multFieldLine}>
                                    <Field as={TextField} fullWidth label="Token" name="token" variant="outlined" margin="dense" error={touched.token && Boolean(errors.token)} helperText={touched.token && errors.token}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        color="primary"
                                                        aria-label="generate token"
                                                        onClick={() => handleGenerateToken(setFieldValue)}
                                                    >
                                                        <Cached />
                                                    </IconButton>
                                                    <IconButton
                                                        color="primary"
                                                        aria-label="copy token"
                                                        onClick={() => handleCopyToken(values.token)}
                                                    >
                                                        <FileCopy />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </div>
                                <div className={classes.multFieldLine}>
                                    <FormControlLabel
                                        control={<Field as={Switch} color="primary" name="enabled" checked={values.enabled} />}
                                        label="Enabled"
                                    />
                                    <FormControlLabel
                                        control={<Field as={Switch} color="primary" name="status" checked={values.status} />}
                                        label="Status"
                                    />
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleClose} color="secondary" disabled={isSubmitting} variant="outlined">
                                    {i18n.t("global.buttons.cancel")}
                                </Button>
                                <Button type="submit" color="primary" disabled={isSubmitting} variant="contained" className={classes.btnWrapper}>
                                    {localeServerId ? i18n.t("global.buttons.okEdit") : i18n.t("global.buttons.okAdd")}
                                    {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default React.memo(LocaleServerModal);
